import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { FileDown, FileText, Crown, Download, Eye, Calendar, DollarSign, Home, Activity } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DealPacketsPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [packetData, setPacketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const tier = subscription?.tier || "starter";

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    } finally {
      setLoading(false);
    }
  };

  const generatePacket = async (dealId) => {
    setGenerating(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deals/${dealId}/packet`, { withCredentials: true });
      setPacketData(response.data);
      setSelectedDeal(deals.find(d => d.deal_id === dealId));
      toast.success("Deal packet generated");
    } catch (error) {
      toast.error("Failed to generate packet");
    } finally {
      setGenerating(false);
    }
  };

  const exportToPDF = (deckType = "investor") => {
    if (!packetData || !selectedDeal) return;

    const doc = new jsPDF();
    const { deal, scenarios, activities } = packetData;
    let y = 20;
    
    // Colors
    const gold = [212, 175, 55];
    const darkBg = [24, 24, 27];
    const lightText = [161, 161, 170];

    // Cover Page
    doc.setFillColor(...darkBg);
    doc.rect(0, 0, 210, 297, 'F');
    
    // Gold accent bar
    doc.setFillColor(...gold);
    doc.rect(0, 80, 210, 2, 'F');
    doc.rect(0, 215, 210, 2, 'F');
    
    // Title
    doc.setTextColor(...gold);
    doc.setFontSize(32);
    doc.setFont("helvetica", "bold");
    doc.text("INVESTMENT", 105, 110, { align: "center" });
    doc.text("MEMORANDUM", 105, 125, { align: "center" });
    
    // Property Address
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text(deal.property_address || "Property Address", 105, 150, { align: "center" });
    
    doc.setFontSize(12);
    doc.setTextColor(...lightText);
    doc.text(`${deal.city || ""}, ${deal.state || ""} ${deal.zip_code || ""}`, 105, 162, { align: "center" });
    
    // Key Metrics Bar
    doc.setFontSize(10);
    doc.setTextColor(...gold);
    doc.text("ASKING PRICE", 35, 185, { align: "center" });
    doc.text("PROJECTED ROI", 105, 185, { align: "center" });
    doc.text("STRATEGY", 175, 185, { align: "center" });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(`$${(deal.asking_price || 0).toLocaleString()}`, 35, 195, { align: "center" });
    
    const bestScenario = scenarios?.[0];
    const roi = bestScenario?.roi || bestScenario?.cash_on_cash_return || 0;
    doc.text(`${roi.toFixed(1)}%`, 105, 195, { align: "center" });
    doc.text((bestScenario?.strategy || "Analysis").toUpperCase(), 175, 195, { align: "center" });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(...lightText);
    doc.text("CONFIDENTIAL - FOR QUALIFIED INVESTORS ONLY", 105, 250, { align: "center" });
    doc.text(`Prepared by DealForge | ${new Date().toLocaleDateString()}`, 105, 258, { align: "center" });
    
    // ======== PAGE 2: Property Details ========
    doc.addPage();
    doc.setFillColor(...darkBg);
    doc.rect(0, 0, 210, 297, 'F');
    
    // Section Header
    doc.setFillColor(...gold);
    doc.rect(15, 15, 3, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("PROPERTY OVERVIEW", 25, 27);
    
    y = 50;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    // Property details
    const details = [
      ["Property Type", deal.property_type || "N/A"],
      ["Bedrooms", deal.bedrooms || 0],
      ["Bathrooms", deal.bathrooms || 0],
      ["Square Footage", `${(deal.sqft || 0).toLocaleString()} sqft`],
      ["Lot Size", deal.lot_size || "N/A"],
      ["Year Built", deal.year_built || "N/A"],
      ["Pipeline Stage", (deal.pipeline_stage || "").replace(/_/g, ' ').toUpperCase()]
    ];
    
    details.forEach(([label, value]) => {
      doc.setTextColor(...lightText);
      doc.text(label, 25, y);
      doc.setTextColor(255, 255, 255);
      doc.text(String(value), 80, y);
      y += 8;
    });
    
    // ======== PAGE 3: Financial Analysis ========
    if (scenarios && scenarios.length > 0) {
      doc.addPage();
      doc.setFillColor(...darkBg);
      doc.rect(0, 0, 210, 297, 'F');
      
      doc.setFillColor(...gold);
      doc.rect(15, 15, 3, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("FINANCIAL ANALYSIS", 25, 27);
      
      y = 50;
      scenarios.slice(0, 3).forEach((scenario, idx) => {
        doc.setFillColor(40, 40, 45);
        doc.roundedRect(15, y - 5, 180, 45, 2, 2, 'F');
        
        doc.setTextColor(...gold);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(`SCENARIO ${idx + 1}: ${(scenario.strategy || "").toUpperCase()}`, 25, y + 5);
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        
        const metrics = [];
        if (scenario.mao) metrics.push(`MAO: $${scenario.mao.toLocaleString()}`);
        if (scenario.profit) metrics.push(`Profit: $${scenario.profit.toLocaleString()}`);
        if (scenario.monthly_cashflow) metrics.push(`Cash Flow: $${scenario.monthly_cashflow.toLocaleString()}/mo`);
        if (scenario.roi) metrics.push(`ROI: ${scenario.roi.toFixed(1)}%`);
        
        doc.text(metrics.join("  |  "), 25, y + 18);
        
        y += 55;
      });
    }
    
    // ======== PAGE 4: Activity Timeline ========
    if (activities && activities.length > 0) {
      doc.addPage();
      doc.setFillColor(...darkBg);
      doc.rect(0, 0, 210, 297, 'F');
      
      doc.setFillColor(...gold);
      doc.rect(15, 15, 3, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("TRANSACTION ACTIVITY", 25, 27);
      
      y = 50;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      
      activities.slice(0, 15).forEach(activity => {
        if (y > 270) { doc.addPage(); y = 20; }
        const date = new Date(activity.created_at).toLocaleDateString();
        doc.setTextColor(...gold);
        doc.text(`[${date}]`, 25, y);
        doc.setTextColor(...lightText);
        doc.text(activity.content || activity.type, 55, y);
        y += 7;
      });
    }
    
    // Final page - Disclaimer
    doc.addPage();
    doc.setFillColor(...darkBg);
    doc.rect(0, 0, 210, 297, 'F');
    
    doc.setFillColor(...gold);
    doc.rect(15, 15, 3, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("DISCLAIMER", 25, 27);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...lightText);
    
    const disclaimer = [
      "This Investment Memorandum is provided for informational purposes only and does not",
      "constitute an offer to sell or a solicitation of an offer to buy any securities or investment",
      "products. Past performance is not indicative of future results.",
      "",
      "All financial projections, estimates, and analyses contained herein are based on assumptions",
      "that may not be realized. Actual results may vary materially from those presented.",
      "",
      "Prospective investors should conduct their own due diligence and consult with qualified",
      "legal, tax, and financial advisors before making any investment decisions.",
      "",
      "Generated by DealForge - The Real Estate Transaction Operating System"
    ];
    
    y = 50;
    disclaimer.forEach(line => {
      doc.text(line, 25, y);
      y += 6;
    });

    doc.save(`Investment_Memorandum_${deal.property_address?.replace(/[^a-z0-9]/gi, '_') || 'export'}.pdf`);
    toast.success("Investment Memorandum exported");
  };

  const formatCurrency = (val) => `$${(val || 0).toLocaleString()}`;

  // Feature gate removed - all active subscribers have full access
  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} flex-1 p-4 md:p-8`}>
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white uppercase">Deck Generator</h1>
          <p className="text-white/70 mt-1">Generate professional pitch decks for investors and stakeholders</p>
        </div>

        {/* Deal Selector */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
          <h2 className="text-white font-bold mb-4">Select a Deal</h2>
          <div className="flex gap-4">
            <Select onValueChange={(val) => generatePacket(val)}>
              <SelectTrigger className="bg-black border-white/[0.08] text-white w-96">
                <SelectValue placeholder="Choose a deal to generate packet..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                {deals.map((deal) => (
                  <SelectItem key={deal.deal_id} value={deal.deal_id} className="text-white hover:bg-white/[0.06]">
                    {deal.property_address} - {formatCurrency(deal.asking_price)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {generating && <span className="text-gold animate-pulse">Generating...</span>}
          </div>
        </div>

        {/* Packet Preview */}
        {packetData && selectedDeal && (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
              <h2 className="text-white font-bold text-lg">Investment Memorandum Preview</h2>
              <Button onClick={() => exportToPDF("investor")} className="gold-gradient-btn rounded-none font-bold">
                <Download className="w-4 h-4 mr-2" />Export Pitch Deck
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Property Header */}
              <div className="text-center border-b border-white/[0.08] pb-6">
                <h3 className="text-2xl font-bold text-gold mb-2">{selectedDeal.property_address}</h3>
                <p className="text-white/70">{selectedDeal.city}, {selectedDeal.state} {selectedDeal.zip_code}</p>
              </div>

              {/* Property Details Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-black p-4 border border-white/[0.08]">
                  <h4 className="text-gold font-bold mb-3 flex items-center gap-2">
                    <Home className="w-4 h-4" />Property Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-white/70">Type</span><span className="text-white">{selectedDeal.property_type}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Beds/Baths</span><span className="text-white">{selectedDeal.bedrooms} / {selectedDeal.bathrooms}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Sqft</span><span className="text-white">{selectedDeal.sqft?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Status</span><span className="text-white capitalize">{selectedDeal.status}</span></div>
                  </div>
                </div>

                <div className="bg-black p-4 border border-white/[0.08]">
                  <h4 className="text-gold font-bold mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />Financials
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-white/70">Asking Price</span><span className="text-white font-mono">{formatCurrency(selectedDeal.asking_price)}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Price/Sqft</span><span className="text-white font-mono">{formatCurrency(selectedDeal.sqft ? selectedDeal.asking_price / selectedDeal.sqft : 0)}</span></div>
                  </div>
                </div>
              </div>

              {/* Scenarios */}
              {packetData.scenarios?.length > 0 && (
                <div>
                  <h4 className="text-gold font-bold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />Deal Scenarios ({packetData.scenarios.length})
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {packetData.scenarios.map((scenario, idx) => (
                      <div key={idx} className="bg-black p-4 border border-white/[0.08]">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-white font-bold">{scenario.name || `Scenario ${idx + 1}`}</span>
                          <span className="text-xs bg-gold/20 text-gold px-2 py-0.5">{scenario.strategy}</span>
                        </div>
                        {scenario.mao && <p className="text-sm text-white/70">MAO: <span className="text-white font-mono">{formatCurrency(scenario.mao)}</span></p>}
                        {scenario.monthly_cashflow && <p className="text-sm text-white/70">Cash Flow: <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-mono">{formatCurrency(scenario.monthly_cashflow)}/mo</span></p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity */}
              {packetData.activities?.length > 0 && (
                <div>
                  <h4 className="text-gold font-bold mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />Recent Activity
                  </h4>
                  <div className="bg-black border border-white/[0.08] divide-y divide-white/[0.06] max-h-64 overflow-y-auto">
                    {packetData.activities.slice(0, 10).map((activity, idx) => (
                      <div key={idx} className="p-3 flex justify-between text-sm">
                        <span className="text-white/70">{activity.content || activity.type}</span>
                        <span className="text-white/70">{new Date(activity.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Generated timestamp */}
              <p className="text-center text-white/70 text-xs pt-4 border-t border-white/[0.08]">
                Generated by DealForge on {new Date(packetData.generated_at).toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!packetData && !loading && (
          <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]">
            <FileDown className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white/70 mb-2">Select a Deal Above</h2>
            <p className="text-white/70">Choose a deal to generate its comprehensive packet</p>
          </div>
        )}
      </main>
    </div>
  );
}
